import { useState, type ReactNode } from 'react'

/** Per-page collapsed flag, remembered across sessions (Safari private browsing can throw -
 *  mirrors the app's other localStorage helpers, e.g. tm-sidebar-collapsed, tm-theme). */
const storageKey = (pageKey: string) => `tm-intro-collapsed-${pageKey}`

function readCollapsed(pageKey: string): boolean {
  try {
    return localStorage.getItem(storageKey(pageKey)) === '1'
  } catch {
    return false
  }
}

function writeCollapsed(pageKey: string, collapsed: boolean): void {
  try {
    localStorage.setItem(storageKey(pageKey), collapsed ? '1' : '0')
  } catch { /* storage unavailable - the choice just won't persist */ }
}

interface Props {
  /** Unique per-page key for the persisted collapsed flag (e.g. 'providers', 'modules', 'state'). */
  pageKey: string
  title: string
  children: ReactNode
}

/**
 * A dismissible "what is this page for" explainer, collapsing to a single pill that re-expands on
 * click. The collapsed/expanded choice is remembered per page (not just per session), so a user
 * who has already read it doesn't see it again on return visits, but can always bring it back.
 */
export function PageIntro({ pageKey, title, children }: Props) {
  const [collapsed, setCollapsed] = useState(() => readCollapsed(pageKey))

  function toggle() {
    setCollapsed((c) => {
      const next = !c
      writeCollapsed(pageKey, next)
      return next
    })
  }

  if (collapsed) {
    return (
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={toggle}
        aria-expanded={false}
        style={{ marginBottom: 16, display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text2)' }}
      >
        <span aria-hidden>▸</span> {title}
      </button>
    )
  }

  return (
    <div className="card fu" style={{ marginBottom: 20 }}>
      <div className="card-header">
        <div>
          <div className="kicker">// about this page</div>
          <h2 style={{ fontSize: '0.95rem' }}>{title}</h2>
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={toggle}
          aria-expanded={true}
          aria-label="Hide this explanation"
        >
          Hide <span aria-hidden>▾</span>
        </button>
      </div>
      <div className="card-body" style={{ fontFamily: 'var(--body)', fontSize: '0.82rem', color: 'var(--text2)', lineHeight: 1.7 }}>
        {children}
      </div>
    </div>
  )
}
